import type { AccessDetailsDialogProps } from '@confluence/space-access-details';

import type { CloseConfirmationDialogProps } from '../controlled/CloseConfirmationDialog';
import type { DialogsProps } from '../controlled/Dialogs';
import type { GroupNotifyWarningDialogProps } from '../controlled/GroupNotifyWarningDialog';
import type { ShareAndRestrictDialogProps } from '../controlled/shareAndRestrictDialog/ShareAndRestrictDialog';
import type { InspectPermsDialogProps } from '../controlled/shareAndRestrictDialog/header/OverflowMenu';
import type { RestrictedAccessWarningDialogProps } from '../controlled/resolveRestrictions/RestrictedAccessWarningDialog';
import type { ResolveRestrictionsDialogProps } from '../controlled/resolveRestrictions/ResolveRestrictionsDialog';
import type { OpenAccessWarningDialogProps } from '../controlled/openAccessWarning/OpenAccessWarningDialog';

import { makeHeaderProps, makeGeneralAccessProps } from './makeHeaderProps';
import { makeBodyProps } from './makeBodyProps';
import { makeFooterProps } from './makeFooterProps';
import { type StatesAndActions, SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE } from './Controller';

export function makeDialogsProps(statesAndActions: StatesAndActions): DialogsProps {
	const {
		masterState: {
			sessionState,
			isCloseConfirmationOpen,
			isSpaceAccessDetailsOpen,
			isInspectPermsDialogOpen,
			isOpenAccessWarningDialogOpen,
		},
	} = statesAndActions;

	return {
		shareAndRestrictDialog:
			sessionState === 'shareAndRestrict'
				? makeShareAndRestrictDialogProps(statesAndActions)
				: undefined,
		restrictedAccessWarningDialog:
			sessionState === 'warnRestrictedAccess'
				? makeRestrictedAccessWarningDialog(statesAndActions)
				: undefined,
		groupNotifyWarningDialog:
			sessionState === 'confirmGroupNotifications'
				? makeGroupNotifyWarningDialog(statesAndActions)
				: undefined,
		resolveRestrictionsDialog:
			sessionState === 'resolveRestrictions'
				? makeResolveRestrictionsDialogProps(statesAndActions)
				: undefined,
		closeConfirmationDialog: isCloseConfirmationOpen
			? makeCloseConfirmationProps(statesAndActions)
			: undefined,
		spaceAccessDetails: isSpaceAccessDetailsOpen
			? makeSpaceAccessDetailsProps(statesAndActions)
			: undefined,
		inspectPermsDialog: isInspectPermsDialogOpen
			? makeInspectPermsDialogProps(statesAndActions)
			: undefined,
		openAccessWarningDialog: isOpenAccessWarningDialogOpen
			? makeOpenAccessWarningDialogProps(statesAndActions)
			: undefined,
		// close confirmation, public linkDialogProps, etc to follow
	};
}

function makeShareAndRestrictDialogProps(
	statesAndActions: StatesAndActions,
): ShareAndRestrictDialogProps {
	const {
		actions: { dismiss, potentialFinalDialogDidCompleteClose },
		masterState: { isPublicLinkDialogOpen, isShareToSlackDialogOpen, isAccessRequestsDialogOpen },
		derivedState: { isUnpublishedDraft, stagedGeneralAccessLevel },
		queriesState: { hasInheritedRestrictions },
		controllerProps: { contentId, contentType, contentSubType, pageMode, openFrom },
	} = statesAndActions;

	const isAnotherDialogOpen =
		isPublicLinkDialogOpen || isShareToSlackDialogOpen || isAccessRequestsDialogOpen;

	const hideFooter = isAnotherDialogOpen;

	return {
		header: makeHeaderProps(statesAndActions),
		body: makeBodyProps(statesAndActions),
		footer: hideFooter ? undefined : makeFooterProps(statesAndActions),
		onCloseRequest: dismiss,
		onCloseComplete: potentialFinalDialogDidCompleteClose,
		analyticsAttributes: {
			contentId,
			contentType,
			contentSubType,
			isLivePage: contentSubType === 'live',
			isDraft: isUnpublishedDraft,
			pageMode,
			generalAccessLevel: stagedGeneralAccessLevel,
			hasInheritedRestrictions,
			openFrom,
		},
	};
}

function makeGroupNotifyWarningDialog(
	statesAndActions: StatesAndActions,
): GroupNotifyWarningDialogProps {
	const {
		masterState: { isSaving },
		derivedState: { stagedNotificationRecipients },
		actions: {
			doNotifyGroupsConfirmed,
			doNotNotifyGroupsConfirmed,
			potentialFinalDialogDidCompleteClose,
		},
	} = statesAndActions;

	const groupNames = stagedNotificationRecipients
		.filter(({ type }) => type === 'group')
		.map(({ name }) => name);

	return {
		groupNames,
		isSaving,
		onDoNotify: doNotifyGroupsConfirmed,
		onDoNotNotify: doNotNotifyGroupsConfirmed,
		onCloseComplete: potentialFinalDialogDidCompleteClose,
	};
}

function makeRestrictedAccessWarningDialog(
	statesAndActions: StatesAndActions,
): RestrictedAccessWarningDialogProps {
	const {
		controllerProps: { pageTitle },
		masterState: { restrictedUserPrincipals },
		actions: { ignoreRestrictionsAndContinue, potentialFinalDialogDidCompleteClose },
	} = statesAndActions;

	return {
		restrictedUserPrincipals,
		pageTitle,
		onSkip: ignoreRestrictionsAndContinue,
		onCloseComplete: potentialFinalDialogDidCompleteClose,
	};
}

function makeCloseConfirmationProps(
	statesAndActions: StatesAndActions,
): CloseConfirmationDialogProps {
	const {
		actions: { dismissCloseConfirmationDialog, closeWithoutSaving },
		controllerProps: { focusRef },
	} = statesAndActions;
	return {
		onCancel: dismissCloseConfirmationDialog,
		onDiscard: closeWithoutSaving,
		focusRef,
	};
}

function makeSpaceAccessDetailsProps(statesAndActions: StatesAndActions): AccessDetailsDialogProps {
	const { controllerProps, actions, queriesState } = statesAndActions;
	return {
		onClose: actions.dismissSpaceAccessDetails,
		accessDetailsParams: {
			restrictingContentId: controllerProps.contentId,
			spaceKey: queriesState.spaceKey,
			hasInheritedRestrictions: queriesState.hasInheritedRestrictions,
			experience: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
		},
	};
}

function makeInspectPermsDialogProps(statesAndActions: StatesAndActions): InspectPermsDialogProps {
	const { controllerProps, actions } = statesAndActions;
	return {
		onClose: actions.dismissInspectPermsDialog,
		contentId: controllerProps.contentId,
	};
}

function makeResolveRestrictionsDialogProps(
	statesAndActions: StatesAndActions,
): ResolveRestrictionsDialogProps {
	const {
		controllerProps: { pageTitle, contentId },
		masterState: { restrictedUserPrincipals, addMode },
		actions: {
			resolveRestrictionsComplete,
			potentialFinalDialogDidCompleteClose,
			ignoreRestrictionsAndContinue,
			showErrorFlag,
		},
	} = statesAndActions;

	return {
		contentId,
		pageTitle,
		restrictedUserPrincipals,
		addMode,
		onComplete: resolveRestrictionsComplete,
		onCloseComplete: potentialFinalDialogDidCompleteClose,
		onSkip: ignoreRestrictionsAndContinue,
		showErrorFlag,
	};
}

function makeOpenAccessWarningDialogProps(
	statesAndActions: StatesAndActions,
): OpenAccessWarningDialogProps {
	const {
		actions: { closeOpenAccessWarningDialog, handleOpenAccessWarningDialogConfirmation },
	} = statesAndActions;

	return {
		generalAccess: makeGeneralAccessProps(statesAndActions),
		onCancel: closeOpenAccessWarningDialog,
		onOpenAccess: handleOpenAccessWarningDialogConfirmation,
	};
}
